/** An action as far as the notice reads it: the zone in which answering it needs `workflows.confirm`. */
interface ZonedAction {
  readonly confirm_zone_name?: string | null;
}

/** The translation function, as far as the notice uses it. */
type Translate = (key: string, options?: Record<string, string>) => string;

/**
 * The zones in which answering the actions needs `workflows.confirm` (nap-backend#1951), each once, in action order. An
 * action without a zone, or from a backend that sends none, names none.
 */
export const confirmZoneNames = (actions: readonly ZonedAction[]): string[] => [
  ...new Set(actions.map((action) => action.confirm_zone_name).filter((name): name is string => typeof name === 'string' && name !== '')),
];

/**
 * The notice for a caller who may not answer some of a message's waiting actions: it names the zones of the given
 * actions, those the caller may not answer, so they can tell which privilege they lack where. When they may answer the
 * other waiting actions (`answersOthers`), it says only some can be answered; otherwise that none can. Without a zone
 * name it keeps the general text. The zone is the waiting node's, which for an action added from another zone's queue
 * is not the zone the inbox shows for the message.
 */
export const cannotConfirmText = (t: Translate, actions: readonly ZonedAction[], answersOthers = false): string => {
  const zones = confirmZoneNames(actions);
  const key = answersOthers ? 'workflows.cannot_confirm_some' : 'workflows.cannot_confirm';
  return zones.length > 0 ? t(`${key}_in`, { zones: zones.join(', ') }) : t(key);
};
